import React, { useState, useEffect } from 'react';
import { Table, Input, Progress, Tag, Tooltip, Modal, Button, DatePicker } from 'antd';
import { SearchOutlined, FolderOutlined, FileOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const LogsTable = ({ activeUploads, visible, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://files-upload.finkraftai.com/get-upload-logs');
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) fetchLogs();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (activeUploads.some(u => u.progress >= 100)) fetchLogs();
    }, 2000);
    return () => clearInterval(interval);
  }, [activeUploads, visible]);

  const formatDateTime = (date, time) => {
    if (!date || !time) return '';
    const dt = new Date(`${date}T${time}`);
    return dt.toLocaleString('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const userEmail = localStorage.getItem('login_email') || 'Unknown User';
  const activeRows = activeUploads.map(upload => ({
    key: `active-${upload.id}`,
    name: upload.folderName || (upload.files?.[0]?.name ?? 'Unknown'),
    client_name: upload.clientName,
    user_name: userEmail,
    is_folder: !!upload.folderName,
    status: upload.progress >= 100 ? 'SUCCESS' : 'PROCESSING',
    progress: upload.progress,
    isd_date: new Date().toISOString().split('T')[0],
    time: new Date().toISOString().split('T')[1].slice(0, 8)
  }));

  const mergedData = [
    ...activeRows,
    ...logs
      .filter(log => !activeRows.some(a => a.key === `active-${log.id}`))
      .map(log => ({
        ...log,
        progress: log.status === 'SUCCESS' ? 100 : 0
      }))
  ];

  // ✅ Manual filtering for both search + single/range date
  const filteredData = mergedData.filter(record => {
    const search = searchText.toLowerCase();
    const recordDate = dayjs(record.isd_date);

    let isInDateRange = true;
    if (dateRange) {
      if (dateRange.length === 1 || !dateRange[1]) {
        isInDateRange = recordDate.isSame(dateRange[0], 'day');
      } else if (dateRange.length === 2) {
        isInDateRange =
          recordDate.isSame(dateRange[0], 'day') ||
          recordDate.isSame(dateRange[1], 'day') ||
          (recordDate.isAfter(dateRange[0].startOf('day')) &&
           recordDate.isBefore(dateRange[1].endOf('day')));
      }
    }

    return (
      isInDateRange &&
      (
        record.name?.toLowerCase().includes(search) ||
        record.client_name?.toLowerCase().includes(search) ||
        record.user_name?.toLowerCase().includes(search) ||
        record.status?.toLowerCase().includes(search)
      )
    );
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          maxWidth: 300,
          lineHeight: '1.3em'
        }}>
          {record.is_folder ? (
            <FolderOutlined style={{ color: '#1890ff', fontSize: 18, flexShrink: 0 }} />
          ) : (
            <FileOutlined style={{ color: '#52c41a', fontSize: 18, flexShrink: 0 }} />
          )}
          <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{text}</span>
        </div>
      ),
    },
    { title: 'Client', dataIndex: 'client_name', key: 'client_name' },
    { title: 'User', dataIndex: 'user_name', key: 'user_name' },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Upload Date/Time
          <RangePicker
            onChange={dates => setDateRange(dates)}
            value={dateRange}
            allowClear
            format="YYYY-MM-DD"
            style={{ marginLeft: 8 }}
            placeholder={['Start Date', 'End Date']}
          />
        </div>
      ),
      key: 'datetime',
      render: (_, record) => formatDateTime(record.isd_date, record.time)
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.status === 'PROCESSING') {
          return (
            <Progress
              percent={record.progress}
              status="active"
              strokeColor={{ '0%': '#108ee9', '100%': '#52c41a' }}
              size="small"
              strokeWidth={8}
              style={{ borderRadius: 6 }}
            />
          );
        }
        return (
          <Tag
            color={record.status === 'SUCCESS' ? 'green' : 'red'}
            style={{ fontWeight: 600, padding: '0 12px', borderRadius: 6 }}
          >
            {record.status}
          </Tag>
        );
      },
    },
  ];

  return (
    <Modal
  title={<span style={{ fontWeight: 600, fontSize: 18 }}>Upload Logs</span>}
  open={visible}
  onCancel={onClose}
  width={1400}
  bodyStyle={{ padding: 24, background: '#f9f9f9' }}
  style={{ top: 20 }}
  footer={
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
    
    </div>
  }
  centered
>

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Input
          placeholder="Search logs..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ width: 320, borderRadius: 8, boxShadow: '0 1px 6px rgba(0,0,0,0.1)' }}
        />
      </div>

<Table
  columns={columns}
  dataSource={filteredData}
  rowKey={record => record.key || `${record.name}-${record.isd_date}-${record.time}`}
  loading={loading}
  pagination={{
    showSizeChanger: true,
    showQuickJumper: false,
    pageSizeOptions: ['10', '20', '30', '40', '50'],
    position: ['bottomRight'],
    showTotal: (total) => `Total ${total} items`,
  }}
  scroll={{ y: 450 }}
  bordered
  rowClassName={(record, index) =>
    index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
  }
/>

<style>
  {`
    .table-row-light { background: #ffffff; }
    .table-row-dark { background: #f5f5f5; }
    .ant-table-thead > tr > th { background: #fafafa; font-weight: 600; }
    .ant-table-tbody > tr:hover { background: #e6f7ff !important; }

    /* ✅ Full blue button look for "12 / page" selector */
    .ant-pagination-options-size-changer {
      background-color: #1677ff !important; /* Solid blue */
      color: #ffffff !important; /* White text */
      border-radius: 8px;
      padding: 2px 12px !important;
      font-weight: 600;
      border: none !important;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      cursor: pointer;
      transition: all 0.2s ease-in-out;
    }

    .ant-pagination-options-size-changer:hover {
      background-color: #0958d9 !important; /* Darker blue hover */
      transform: translateY(-1px);
    }

    /* Dropdown inside remains white text */
    .ant-select-selector {
      background-color: transparent !important;
      color: #ffffff !important;
      border: none !important;
      box-shadow: none !important;
    }

    /* Make dropdown arrow white */
    .ant-select-arrow {
      color: #ffffff !important;
    }

    /* Dropdown menu styling */
    .ant-select-dropdown {
      border-radius: 8px;
      overflow: hidden;
    }

    .ant-select-item-option-selected {
      background-color: #e6f4ff !important;
      color: #1677ff !important;
      font-weight: 600;
    }
  `}
</style>

    </Modal>
  );
};

export default LogsTable;
